import { getCSS, remCSS, useTranslation } from '@gimlite/common';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import {
  Controller,
  ControllerProps,
  FieldError,
  FieldPath,
  FieldValues,
  FormProvider,
  FormState,
  useFormContext,
} from 'react-hook-form';
import { ColorType } from '../../types/component.type';
import { Count } from '../count/count.component';
import { Event } from '../event/event.component';
import { Icon } from '../icon/icon.component';
import { Row } from '../row/row.component';
import { Text, TextType } from '../text/text.component';
import './form.component.scss';
import { Tooltip, TooltipType } from '../tooltip/tooltip.component';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: Pick<
  ControllerProps<TFieldValues, TName>,
  'name' | 'render' | 'control'
>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext() || {};
  const fieldState = getFieldState
    ? getFieldState(fieldContext.name, formState)
    : {};

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...(fieldState as FormState<FieldValues> & { error: FieldError }),
  };
};

type FormItemContextValue = {
  id: string;
  borderStatus?: 'error' | 'success';
};

export const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

export declare namespace FormItemType {
  type Props = {
    className?: string | null;
    children: React.ReactNode | React.ReactNode[];
    label?: string | null;
    tooltipValues?: TooltipType.Props['values'] | null;
    enabledTooltip?: boolean | null;
    required?: boolean;
    borderStatus?: boolean;
    columnExtend?: number;
    displaySpaceError?: boolean;
    errorMessageAlign?: 'flex-start' | 'center' | 'flex-end';
  };
}

const FormItem = ({
  className,
  children,
  label,
  required,
  borderStatus,
  tooltipValues,
  enabledTooltip = false,
  columnExtend = 1,
  displaySpaceError = true,
  errorMessageAlign = 'flex-start',
}: FormItemType.Props) => {
  const id = React.useId();

  const { error, isValid, isValidating, formMessageId } = useFormField() || {};

  const errorMessage = error ? String(error?.message) : null;

  return (
    <FormItemContext.Provider
      value={{
        id,
        borderStatus: borderStatus ? (error ? 'error' : undefined) : undefined,
      }}
    >
      <div
        className={classNames('formItem', className)}
        style={{ gridColumn: `span ${columnExtend}` }}
      >
        <Row
          className="formItem__label"
          alignItems="center"
          justifyContent="flex-start"
          gap={4}
        >
          {label && (
            <Text
              className={'formItem__label__main'}
              variant="subhead"
              color="black_50"
              whiteSpace="nowrap"
            >
              {label}
            </Text>
          )}
          {enabledTooltip && tooltipValues && (
            <Tooltip values={tooltipValues}>
              <Icon color={'black_50'} size="s" variant="info2" />
            </Tooltip>
          )}
          {required && (
            <Text
              className="formRequiredField"
              variant="subhead"
              color="black_50"
              whiteSpace="nowrap"
            >
              *
            </Text>
          )}
        </Row>
        <div
          className={classNames('formItem__input', {
            'formItem__input--error': error,
          })}
        >
          {children}
        </div>
        {(errorMessage || (displaySpaceError && !errorMessage)) && (
          <div
            className="formItem__error"
            style={{ justifyContent: errorMessageAlign }}
          >
            <Text color="alert_error" variant="caption">
              {errorMessage}
            </Text>
          </div>
        )}
      </div>
    </FormItemContext.Provider>
  );
};

export declare namespace FormDropDownType {
  type Props = {
    className?: string | null;
    children: React.ReactNode | React.ReactNode[];
    columns?: number;
    title?: string | null;
    displayRequiredFieldLabel?: boolean;
    defaultOpen?: boolean;
    disable?: boolean;
    count?: number | null;
  };
}

export const FormDropDown = observer(
  ({
    className,
    children,
    columns = 3,
    title,
    displayRequiredFieldLabel = true,
    defaultOpen = false,
    disable = false,
    count,
  }: FormDropDownType.Props) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = React.useState(defaultOpen);

    return (
      <div
        className={classNames('formDropDown', className, {
          'formDropDown--disabled': disable,
        })}
      >
        <div
          className="formDropDown__present"
          style={{ marginBottom: isOpen ? remCSS(16) : 0 }}
        >
          <div className="formDropDown__present__title">
            {title && <Text variant={'headline'}>{title?.trim()}</Text>}
            {!!count && <Count variant="dark">{count}</Count>}
          </div>
          <Event onClick={() => setIsOpen(!isOpen)}>
            <div
              className="formDropDown__present__title__icon"
              style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <Icon variant="chevron_up" />
            </div>
          </Event>
        </div>

        {isOpen && (
          <>
            <div
              className={'formDropDown__contain'}
              style={{ gridTemplateColumns: `repeat(${columns || 1}, 1fr)` }}
            >
              {children}
            </div>
            {displayRequiredFieldLabel && (
              <div className="formDropDown__footer">
                <div className="formDropDown__footer__required">
                  <Text variant="subhead" color="black_50">
                    {t('watermelon-requiredFields')}
                  </Text>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

export declare namespace FormContainerType {
  type Props = {
    backgroundColor?: Extract<ColorType, 'black_2' | 'black_4'> | null;
    children?: React.ReactNode | null;
    className?: string | null;
    extra?: React.ReactNode | React.ReactNode[] | null;
    title?: string | null;
    required?: boolean;
  };
}

export const FormContainer = observer(
  ({
    children,
    extra,
    className,
    title,
    backgroundColor = 'black_2',
    required = true,
  }: FormContainerType.Props) => {
    return (
      <div
        className={classNames('formContainer', className)}
        style={{
          backgroundColor: backgroundColor
            ? getCSS(`--color-${backgroundColor}`)
            : 'transparent',
        }}
      >
        <div className="formContainer__header">
          <div className="formContainer__header__title">
            {title && (
              <Text variant="button-m">{`${title} ${required ? '*' : ''}`}</Text>
            )}
          </div>
          <div className="formContainer__header__actions">
            {extra && Array.isArray(extra)
              ? extra.map((item, index) => <div key={index}>{item}</div>)
              : extra}
          </div>
        </div>

        {children && <div className="formContainer__contain">{children}</div>}
      </div>
    );
  },
);

export declare namespace FormSubContainerType {
  type Props = {
    children?: React.ReactNode | null;
    className?: string | null;
    extra?: React.ReactNode | React.ReactNode[] | null;
    title?: string | null;
    backgroundColor?: Extract<ColorType, 'black_2' | 'black_4'> | null;
  };
}

export const FormSubContainer = observer(
  ({
    children,
    extra,
    className,
    title,
    backgroundColor = 'black_4',
  }: FormSubContainerType.Props) => {
    return (
      <div
        className={classNames('formSubContainer', className)}
        style={{
          backgroundColor: backgroundColor
            ? getCSS(`--color-${backgroundColor}`)
            : 'transparent',
        }}
      >
        <div className="formSubContainer__header">
          <div className="formSubContainer__header__title">
            {title && <Text variant="button-m">{title}</Text>}
          </div>
          <div className="formSubContainer__header__actions">
            {extra && Array.isArray(extra)
              ? extra.map((item, index) => <div key={index}>{item}</div>)
              : extra}
          </div>
        </div>

        {children && (
          <div className="formSubContainer__contain">{children}</div>
        )}
      </div>
    );
  },
);

const FormStructureContext = React.createContext<{
  enableSeparator: boolean;
  enableRadius: {
    top: boolean;
    bottom: boolean;
  };
}>({
  enableSeparator: false,
  enableRadius: {
    top: true,
    bottom: true,
  },
});

export declare namespace FormStructureGroupType {
  type Props = {
    children: React.ReactNode | React.ReactNode[];
    className?: string | null;
  };
}

export const FormStructureGroup = observer(
  ({ children, className }: FormStructureGroupType.Props) => {
    const childrenArray = React.Children.toArray(children);

    return (
      <div className={classNames('formStructureGroup', className)}>
        {childrenArray.map((child, index) => (
          <React.Fragment key={index}>
            <FormStructureContext.Provider
              value={{
                enableSeparator: index !== 0 && index < childrenArray.length,
                enableRadius: {
                  top: index === 0,
                  bottom: index === childrenArray.length - 1,
                },
              }}
            >
              {child}
            </FormStructureContext.Provider>
          </React.Fragment>
        ))}
      </div>
    );
  },
);

export declare namespace FormStructureType {
  type Props = {
    className?: string | null;
    children: React.ReactNode | React.ReactNode[];
    columns?: number;
    type?: 'form' | 'text';
    height?: 'min' | 'max';
    title?: string | null;
    titleVariant?: Extract<TextType.Props['variant'], 'title-2' | 'headline'>;
    background?: boolean;
    fullItem?: boolean;
    alignItems?: 'flex-start' | 'center' | 'flex-end';
    displayRequiredFieldLabel?: boolean;
    extra?: React.ReactNode[];
    isClosable?: boolean;
    onClose?: () => void;
  };
}

export const FormStructure = observer(
  ({
    className,
    children,
    columns = 3,
    height = 'max',
    title,
    type = 'form',
    background = true,
    fullItem = false,
    alignItems = 'flex-start',
    displayRequiredFieldLabel = true,
    titleVariant = 'title-2',
    extra,
    isClosable = false,
    onClose,
  }: FormStructureType.Props) => {
    const { t } = useTranslation();

    return (
      <FormStructureContext.Consumer>
        {({ enableSeparator, enableRadius }) => (
          <div
            style={{
              height: height === 'min' ? 'initial' : '100%',
              overflowY: height === 'min' ? 'visible' : 'auto',
              backgroundColor: background
                ? getCSS('--color-black_4')
                : 'transparent',
              padding: background ? remCSS(32) : '0',
              borderWidth: isClosable ? '1px' : '0',
            }}
            className={classNames(
              'formStructure',
              {
                'formStructure--radius--top': enableRadius.top,
                'formStructure--radius--bottom': enableRadius.bottom,
              },
              className,
            )}
          >
            {enableSeparator && (
              <div className="formStructure__separator"></div>
            )}
            {(title || (extra && extra.length > 0)) && (
              <div
                style={{
                  marginBottom: type === 'form' ? remCSS(16) : remCSS(8),
                }}
                className="formStructure__present"
              >
                {title && <Text variant={titleVariant}>{title}</Text>}
                {(extra || isClosable) && (
                  <div className="formStructure__present__extra">
                    {(extra || []).map((item, index) => (
                      <div key={index}>{item}</div>
                    ))}
                    {isClosable && (
                      <Event onClick={onClose}>
                        <Icon color={'deep_indigo'} variant="close" />
                      </Event>
                    )}
                  </div>
                )}
              </div>
            )}
            <div
              className={classNames('formStructure__contain', {
                'formStructure__contain--fullItem': fullItem,
              })}
              style={{
                gridTemplateColumns: !fullItem
                  ? `repeat(${columns || 1}, 1fr)`
                  : '1fr',
                alignItems: alignItems,
              }}
            >
              {children}
            </div>

            {displayRequiredFieldLabel && (
              <div className="formStructure__footer">
                <div className="formStructure__footer__required">
                  <Text variant="subhead" color="black_50">
                    {t('watermelon-requiredFields')}
                  </Text>
                </div>
              </div>
            )}
          </div>
        )}
      </FormStructureContext.Consumer>
    );
  },
);

export { Form, FormField, FormItem, useFormField };
